import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, ListChecks, Wallet, Users, ChevronRight, Sparkles } from 'lucide-react';
import { listCategories, listBudgetCats, listGuests, getSavings, formatRupiah } from '../api';
import { useAuth } from '../context/AuthContext';

export default function Beranda() {
  const navigate = useNavigate();
  const { couple } = useAuth();
  const [cats, setCats] = useState([]);
  const [budgetCats, setBudgetCats] = useState([]);
  const [guests, setGuests] = useState([]);
  const [savings, setSavings] = useState({ collected: 0, target: 0 });

  useEffect(() => {
    Promise.all([listCategories(), listBudgetCats(), listGuests(), getSavings()])
      .then(([c, b, g, s]) => { setCats(c); setBudgetCats(b); setGuests(g); setSavings(s); })
      .catch(() => {});
  }, []);

  const daysLeft = useMemo(() => {
    if (!couple?.akadDate) return 0;
    const diff = new Date(couple.akadDate).getTime() - Date.now();
    return Math.max(0, Math.ceil(diff / 86400000));
  }, [couple]);

  const totalTasks = cats.reduce((a,c) => a + (c.total||0), 0);
  const doneTasks = cats.reduce((a,c) => a + (c.done||0), 0);
  const percentTasks = totalTasks ? Math.round((doneTasks/totalTasks)*100) : 0;

  const totalBudget = Number(couple?.totalBudget) || 0;
  const totalRealisasi = budgetCats.reduce((a,c) => a + (c.realisasi||0), 0);
  const percentBudget = totalBudget ? Math.min(100, Math.round((totalRealisasi/totalBudget)*100)) : 0;

  const totalHead = guests.reduce((a,g) => a + (g.headCount||1), 0);
  const totalHadir = guests.filter(g => g.status === 'Hadir').reduce((a,g) => a + (g.headCount||1), 0);

  const akadFormatted = couple?.akadDate ? new Date(couple.akadDate).toLocaleDateString('id-ID', { day:'numeric', month:'long', year:'numeric' }) : '-';

  return (
    <div className="px-5 pt-8 lg:pt-10">
      <div className="mb-6">
        <div className="text-xs text-terracotta-500 tracking-widest font-medium mb-1">SELAMAT DATANG</div>
        <h1 className="font-serif text-3xl text-sage-700">Halo, {couple?.brideName || '...'} &amp; {couple?.groomName || '...'} 💝</h1>
      </div>

      <div className="relative rounded-3xl overflow-hidden bg-gradient-to-br from-terracotta-500 to-terracotta-600 p-7 text-white mb-6">
        <div className="absolute inset-0 opacity-10" style={{backgroundImage: 'radial-gradient(circle at 30% 20%, white 1px, transparent 1px)', backgroundSize: '24px 24px'}} />
        <div className="relative flex items-start justify-between">
          <div>
            <div className="text-xs tracking-widest text-cream-100 mb-2">HARI-H</div>
            <div className="font-serif text-6xl md:text-7xl">{daysLeft}</div>
            <div className="text-cream-100 text-sm mt-1">hari lagi menuju akad</div>
            <div className="mt-4 text-sm text-cream-100 flex items-center gap-2">
              <Calendar className="w-4 h-4" /> {akadFormatted}
            </div>
            {couple?.venue && <div className="text-xs text-cream-200 mt-1">di {couple.venue}</div>}
          </div>
          <Sparkles className="w-10 h-10 text-cream-100/50" />
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4 mb-6">
        <button onClick={() => navigate('/app/checklist')} className="text-left bg-white rounded-2xl p-5 border border-cream-200 hover:border-terracotta-200 hover:shadow-md transition-all">
          <div className="flex items-center justify-between mb-3">
            <div className="w-10 h-10 rounded-xl bg-sage-500 flex items-center justify-center"><ListChecks className="w-5 h-5 text-white" /></div>
            <ChevronRight className="w-5 h-5 text-sage-400" />
          </div>
          <div className="text-sm text-sage-500">Checklist</div>
          <div className="font-serif text-2xl text-sage-700 mt-0.5">{doneTasks}/{totalTasks} tugas</div>
          <div className="mt-3 h-2 bg-cream-100 rounded-full overflow-hidden">
            <div className="h-full bg-terracotta-500" style={{width: `${percentTasks}%`}} />
          </div>
          <div className="text-xs text-terracotta-500 font-medium mt-2">{percentTasks}% selesai</div>
        </button>

        <button onClick={() => navigate('/app/anggaran')} className="text-left bg-white rounded-2xl p-5 border border-cream-200 hover:border-terracotta-200 hover:shadow-md transition-all">
          <div className="flex items-center justify-between mb-3">
            <div className="w-10 h-10 rounded-xl bg-terracotta-500 flex items-center justify-center"><Wallet className="w-5 h-5 text-white" /></div>
            <ChevronRight className="w-5 h-5 text-sage-400" />
          </div>
          <div className="text-sm text-sage-500">Anggaran terpakai</div>
          <div className="font-serif text-2xl text-sage-700 mt-0.5">{formatRupiah(totalRealisasi)}</div>
          <div className="mt-3 h-2 bg-cream-100 rounded-full overflow-hidden">
            <div className="h-full bg-sage-500" style={{width: `${percentBudget}%`}} />
          </div>
          <div className="text-xs text-sage-500 mt-2">dari target {formatRupiah(totalBudget)}</div>
        </button>

        <button onClick={() => navigate('/app/undangan')} className="text-left bg-white rounded-2xl p-5 border border-cream-200 hover:border-terracotta-200 hover:shadow-md transition-all">
          <div className="flex items-center justify-between mb-3">
            <div className="w-10 h-10 rounded-xl bg-sage-600 flex items-center justify-center"><Users className="w-5 h-5 text-white" /></div>
            <ChevronRight className="w-5 h-5 text-sage-400" />
          </div>
          <div className="text-sm text-sage-500">Total tamu</div>
          <div className="font-serif text-2xl text-sage-700 mt-0.5">{totalHead} kepala</div>
          <div className="text-xs text-sage-500 mt-2">{totalHadir} sudah konfirmasi hadir</div>
        </button>

        <div className="bg-white rounded-2xl p-5 border border-cream-200">
          <div className="flex items-center justify-between mb-3">
            <div className="w-10 h-10 rounded-xl bg-terracotta-400 flex items-center justify-center"><Sparkles className="w-5 h-5 text-white" /></div>
          </div>
          <div className="text-sm text-sage-500">Dana terkumpul</div>
          <div className="font-serif text-2xl text-sage-700 mt-0.5">{formatRupiah(savings.collected)}</div>
          <div className="text-xs text-sage-500 mt-2">target {formatRupiah(savings.target || totalBudget)}</div>
        </div>
      </div>

      <div className="mb-8">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-serif text-xl text-sage-700">Kategori Checklist</h2>
          <button onClick={() => navigate('/app/checklist')} className="text-sm text-terracotta-500 font-medium">Lihat semua</button>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {cats.slice(0, 4).map(c => (
            <button key={c.id} onClick={() => navigate(`/app/checklist/${c.id}`)} className="bg-white rounded-2xl p-4 border border-cream-200 hover:border-terracotta-200 text-left">
              <div className="text-2xl mb-2">{c.icon}</div>
              <div className="font-medium text-sage-700 text-sm">{c.name}</div>
              <div className="text-xs text-sage-500 mt-1">{c.done}/{c.total} selesai</div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
